/**
 * ContainersPage (§163) — bin / box / flat / crate / tote / pallet tracking with
 * upstream traceability. List + filter by type/status, create/edit (incl. nesting a
 * bin under a pallet and linking provenance: harvest → block/variety), print QR/barcode
 * labels, and a Trace view showing where a container's fruit came from. Backend §209.
 * Auth-gated → build-verified (tsc/eslint/vite), not preview.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Boxes, X, QrCode, GitBranch, Trash2, Pencil, ShieldAlert } from 'lucide-react'
import {
  containersApi, blocksApi, varietiesApi, harvestsApi,
  type Container, type ContainerProvenance, type RecallReport,
} from '../lib/api'
import { apiError } from '../lib/errors'
import { QRLabelSheet } from '../components/shared/QRLabelSheet'

const TYPES = ['bin', 'box', 'flat', 'crate', 'tote', 'pallet', 'other']
const STATUSES = ['empty', 'filled', 'packed', 'shipped', 'returned', 'retired']
const STATUS_COLOR: Record<string, string> = {
  empty: '#6B7280', filled: '#16a34a', packed: '#2563EB', shipped: '#7C3AED', returned: '#D97706', retired: '#9CA3AF',
}

const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }
const btn = (bg: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: bg, color: '#fff', fontSize: 12.5, fontWeight: 600 })

export default function ContainersPage() {
  const qc = useQueryClient()
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [editing, setEditing] = useState<Container | null>(null)
  const [creating, setCreating] = useState(false)
  const [tracing, setTracing] = useState<Container | null>(null)
  const [recalling, setRecalling] = useState<Container | null>(null)
  const [labelsFor, setLabelsFor] = useState<Container[] | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data, isLoading } = useQuery({
    queryKey: ['containers', typeFilter, statusFilter],
    queryFn: () => containersApi.list({ type: typeFilter, status: statusFilter }).then(r => r.data.containers),
  })
  const containers = data ?? []

  const remove = useMutation({
    mutationFn: (id: string) => containersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['containers'] }),
  })

  const toggle = (id: string) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Boxes size={20} /> Containers
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {selected.size > 0 && (
            <button onClick={() => setLabelsFor(containers.filter(c => selected.has(c.id)))} style={btn('var(--color-text-muted)')}>
              <QrCode size={15} /> Labels ({selected.size})
            </button>
          )}
          <button onClick={() => setCreating(true)} style={btn('var(--color-primary)')}>+ New container</button>
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>
        Track bins, boxes, and pallets from pack-out to shipment. Link each to its harvest so a
        container code traces back to the block, variety, and pick date. Nest bins under a pallet.
      </p>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ ...inp, width: 'auto' }}>
          <option value="">All types</option>
          {TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inp, width: 'auto' }}>
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{cap(s)}</option>)}
        </select>
      </div>

      <div style={card}>
        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>Loading…</p>
        ) : containers.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>No containers yet. Add a bin or pallet to start tracking pack-out.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {containers.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--color-bg)' }}>
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-text)' }}>
                    {c.label || <span style={{ color: 'var(--color-text-faint)' }}>(no label)</span>}
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)' }}>{cap(c.container_type)}</span>
                    {c.container_type === 'pallet' && c.child_count > 0 && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--color-text-faint)' }}>· {c.child_count} bin{c.child_count !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                    {[c.block_name, c.variety_name].filter(Boolean).join(' · ') || '—'}
                    {c.parent_label && <span> · in {c.parent_label}</span>}
                    {c.weight_lb != null && <span> · {c.weight_lb} lb</span>}
                  </div>
                </div>
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color: '#fff', background: STATUS_COLOR[c.status] ?? '#6B7280' }}>{cap(c.status)}</span>
                <button onClick={() => setTracing(c)} title="Trace provenance" style={iconBtn}><GitBranch size={15} /></button>
                <button onClick={() => setRecalling(c)} title="Recall — who got this?" style={iconBtn}><ShieldAlert size={15} /></button>
                <button onClick={() => setEditing(c)} title="Edit" style={iconBtn}><Pencil size={15} /></button>
                <button onClick={() => { if (confirm(`Retire ${c.label || 'this container'}?`)) remove.mutate(c.id) }} title="Retire" style={{ ...iconBtn, color: '#dc2626' }}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {(creating || editing) && (
        <ContainerModal container={editing} containers={containers} onClose={() => { setCreating(false); setEditing(null) }} />
      )}
      {tracing && <TraceModal container={tracing} onClose={() => setTracing(null)} />}
      {recalling && <RecallModal container={recalling} onClose={() => setRecalling(null)} />}
      {labelsFor && (
        <QRLabelSheet
          heading="Container labels"
          action="Print labels"
          items={labelsFor.map(c => ({ url: c.label || c.id, title: c.label || cap(c.container_type), subtitle: [cap(c.container_type), c.block_name, c.variety_name].filter(Boolean).join(' · ') }))}
          onClose={() => setLabelsFor(null)}
        />
      )}
    </main>
  )
}

const iconBtn: React.CSSProperties = { display: 'inline-flex', padding: '5px 8px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-bg-card)', color: 'var(--color-text)', cursor: 'pointer', flexShrink: 0 }
const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s

function ContainerModal({ container, containers, onClose }: { container: Container | null; containers: Container[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    container_type: container?.container_type ?? 'bin',
    label: container?.label ?? '',
    status: container?.status ?? 'empty',
    capacity_lb: container?.capacity_lb?.toString() ?? '',
    weight_lb: container?.weight_lb?.toString() ?? '',
    parent_id: container?.parent_id ?? '',
    block_id: container?.block_id ?? '',
    variety_id: container?.variety_id ?? '',
    harvest_id: container?.harvest_id ?? '',
    notes: container?.notes ?? '',
  })
  const [error, setError] = useState('')
  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }))

  const { data: blocks } = useQuery({ queryKey: ['blocks'], queryFn: () => blocksApi.list().then(r => r.data.blocks) })
  const { data: vars } = useQuery({ queryKey: ['varieties'], queryFn: () => varietiesApi.list().then(r => r.data.varieties) })
  const { data: harvests } = useQuery({ queryKey: ['harvests'], queryFn: () => harvestsApi.list().then(r => r.data.harvests) })
  const pallets = containers.filter(c => c.container_type === 'pallet' && c.id !== container?.id)

  const save = useMutation({
    mutationFn: () => {
      const payload: Partial<Container> = {
        container_type: form.container_type, label: form.label.trim(), status: form.status,
        capacity_lb: form.capacity_lb ? parseFloat(form.capacity_lb) : undefined,
        weight_lb: form.weight_lb ? parseFloat(form.weight_lb) : undefined,
        parent_id: form.parent_id || undefined, block_id: form.block_id || undefined,
        variety_id: form.variety_id || undefined, harvest_id: form.harvest_id || undefined,
        notes: form.notes.trim(),
      }
      return container ? containersApi.update(container.id, payload) : containersApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['containers'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })

  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(560px, 94vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <Header title={container ? 'Edit container' : 'New container'} onClose={onClose} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Type"><select value={form.container_type} onChange={e => set('container_type', e.target.value)} style={inp}>{TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}</select></Field>
          <Field label="Status"><select value={form.status} onChange={e => set('status', e.target.value)} style={inp}>{STATUSES.map(s => <option key={s} value={s}>{cap(s)}</option>)}</select></Field>
          <Field label="Label / barcode"><input value={form.label} onChange={e => set('label', e.target.value)} placeholder="BIN-001" style={inp} /></Field>
          <Field label="On a pallet?"><select value={form.parent_id} onChange={e => set('parent_id', e.target.value)} style={inp}><option value="">— none —</option>{pallets.map(p => <option key={p.id} value={p.id}>{p.label || 'Pallet'}</option>)}</select></Field>
          <Field label="Capacity (lb)"><input type="number" value={form.capacity_lb} onChange={e => set('capacity_lb', e.target.value)} style={inp} /></Field>
          <Field label="Net weight (lb)"><input type="number" value={form.weight_lb} onChange={e => set('weight_lb', e.target.value)} style={inp} /></Field>
        </div>
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'color-mix(in srgb, var(--color-primary) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 14%, transparent)' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 8 }}>PROVENANCE (for traceability)</div>
          <Field label="From harvest (best — links block, variety, date, grade)">
            <select value={form.harvest_id} onChange={e => set('harvest_id', e.target.value)} style={inp}>
              <option value="">— none —</option>
              {(harvests ?? []).map(h => <option key={h.id} value={h.id}>{[h.variety_name, h.harvested_at, h.weight_kg != null ? `${h.weight_kg}kg` : ''].filter(Boolean).join(' · ')}</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
            <Field label="Block"><select value={form.block_id} onChange={e => set('block_id', e.target.value)} style={inp}><option value="">— none —</option>{(blocks ?? []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></Field>
            <Field label="Variety"><select value={form.variety_id} onChange={e => set('variety_id', e.target.value)} style={inp}><option value="">— none —</option>{(vars ?? []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></Field>
          </div>
        </div>
        <div style={{ marginTop: 12 }}><Field label="Notes"><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} /></Field></div>
        {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ ...btn('transparent'), color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending} style={btn('var(--color-primary)')}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Overlay>
  )
}

function TraceModal({ container, onClose }: { container: Container; onClose: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ['container-trace', container.id], queryFn: () => containersApi.trace(container.id).then(r => r.data) })
  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(480px, 94vw)' }}>
        <Header title={`Trace · ${container.label || cap(container.container_type)}`} onClose={onClose} />
        {isLoading || !data ? <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>Loading…</p> : <TraceBody p={data} />}
      </div>
    </Overlay>
  )
}

function TraceBody({ p }: { p: ContainerProvenance }) {
  const row = (k: string, v?: string) => v ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--color-border-tertiary)' }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{k}</span><span style={{ color: 'var(--color-text)', fontWeight: 600 }}>{v}</span>
    </div>
  ) : null
  return (
    <div>
      {p.source === 'none' ? (
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>No provenance linked yet. Edit this container and link a harvest (or a block + variety) so it traces back to the field.</p>
      ) : (
        <>
          <div style={{ fontSize: 11.5, color: 'var(--color-text-faint)', marginBottom: 6 }}>
            Source: {p.source === 'harvest' ? 'harvest record' : 'manual block/variety'}
          </div>
          {row('Block', p.block_name)}
          {row('Variety', p.variety_name)}
          {row('Picked', p.harvested_at)}
          {row('Grade', p.grade)}
          {row('Brix', p.brix != null ? `${p.brix}°Bx` : undefined)}
        </>
      )}
      {p.parent_label && row('On pallet', p.parent_label)}
      {p.children.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 6 }}>CONTAINS ({p.children.length})</div>
          {p.children.map(ch => (
            <div key={ch.id} style={{ fontSize: 12.5, color: 'var(--color-text)', padding: '3px 0' }}>
              {ch.label || cap(ch.container_type)} <span style={{ color: 'var(--color-text-faint)' }}>· {cap(ch.status)}{ch.weight_lb != null ? ` · ${ch.weight_lb} lb` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RecallModal({ container, onClose }: { container: Container; onClose: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ['container-recall', container.id], queryFn: () => containersApi.recall(container.id).then(r => r.data) })
  return (
    <Overlay onClose={onClose}>
      <div style={{ ...card, width: 'min(560px, 94vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <Header title={`Recall · ${container.label || cap(container.container_type)}`} onClose={onClose} />
        {isLoading || !data ? <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>Loading…</p> : <RecallBody r={data} />}
      </div>
    </Overlay>
  )
}

function RecallBody({ r }: { r: RecallReport }) {
  const orders = r.orders ?? []
  return (
    <div>
      <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginBottom: 12 }}>
        If this container is bad, these orders got fruit packed from it — including any bins nested
        under it ({r.containers_searched} container{r.containers_searched !== 1 ? 's' : ''} searched).
      </p>
      {orders.length === 0 ? (
        <div style={{ padding: '14px 12px', borderRadius: 8, background: 'var(--color-bg)', fontSize: 13, color: 'var(--color-text-muted)' }}>
          No orders are linked to this container. Attach containers to an order from the Commerce →
          Orders page (in the order's fulfillment panel) so a recall can trace who received it.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>
            {orders.length} affected order{orders.length !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {orders.map(o => (
              <div key={`${o.order_id}-${o.container_id}`} style={{ padding: '9px 11px', borderRadius: 8, background: 'var(--color-bg)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{o.customer_name || 'Customer'}</span>
                  {o.status && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 999, color: '#fff', background: STATUS_COLOR[o.status] ?? '#6B7280' }}>{cap(o.status)}</span>}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginTop: 2 }}>
                  {[o.customer_email, o.customer_phone].filter(Boolean).join(' · ') || '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 2 }}>
                  {o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}{o.container_label ? ` · from ${o.container_label}` : ''}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      {children}
    </div>
  )
}
function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>{title}</h2>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
    </div>
  )
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block' }}><span style={lbl}>{label}</span>{children}</label>
}
